import { SanityAddToCartButton } from '@data/sanity/queries/types/content'
import { isMarketingConsentGranted } from '@lib/analytics/consent'
import { AnalyticsContext } from '@lib/analytics/context'
import { getProductEventPayload } from '@lib/analytics/events'
import { AnalyticsEventName } from '@lib/analytics/types'
import { CartContext } from '@lib/cart/context'
import { getPrice, getPriceNumber } from '@lib/helpers'
import { ShopContext } from '@lib/shop-context'
import { StringsContext } from '@lib/strings-context'
import cx from 'classnames'
import { ButtonHTMLAttributes, useContext, useState } from 'react'
import Button, { ButtonProps } from './button'

type AddToCartButtonProps = ButtonProps &
  Pick<SanityAddToCartButton, 'productVariant'> & {
    buttonClassName?: string
    onAddToCartError?: () => void
  }

const AddToCartButton = ({
  variant,
  size,
  color,
  icon,
  iconAlignment,
  children,
  className,
  buttonClassName,
  productVariant,
  onAddToCartError,
}: AddToCartButtonProps & ButtonHTMLAttributes<HTMLButtonElement>) => {
  const { cart, openCartAfterAddingItems, addItemsToCart, toggleCart } =
    useContext(CartContext)
  const { currency, currencyCode, taxRate } = useContext(ShopContext)
  const strings = useContext(StringsContext)
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)

  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [isAddToCartError, setIsAddToCartError] = useState(false)

  const handleAddToCart = async () => {
    setIsAddToCartError(false)
    setIsAddingToCart(true)

    const quantity = 1
    const isSuccessful = await addItemsToCart([
      {
        id: productVariant.variantID,
        quantity,
      },
    ])

    if (!isSuccessful && onAddToCartError) {
      onAddToCartError()
    }

    setIsAddingToCart(false)

    if (isSuccessful) {
      // Trigger add to cart event
      triggerAnalyticsEvent(
        AnalyticsEventName.AddToCart,
        getProductEventPayload(
          // TODO: Get product title for `item_name`.
          '',
          [
            {
              variantId: productVariant.variantID,
              // TODO: Get variant options for `item_variant`.
              options: [],
              price: productVariant.price,
              comparePrice: productVariant.comparePrice,
              quantity,
            },
          ],
          currencyCode,
        ),
      )

      // Klaviyo event
      if (window.klaviyo && isMarketingConsentGranted()) {
        const klaviyoProductItem: KlaviyoProductItem = {
          ProductID: `${productVariant.productID}`,
          VariantID: `${productVariant.variantID}`,
          Price: getPriceNumber(productVariant.price),
        }

        if (productVariant.comparePrice) {
          klaviyoProductItem.CompareAtPrice = getPriceNumber(
            productVariant.comparePrice,
          )
        }

        window.klaviyo.track('Added To Cart', klaviyoProductItem)
      }

      // Triple Whale pixel event
      if (window.TriplePixel) {
        window.TriplePixel('AddToCart', {
          item: `${productVariant.productID}`,
          q: quantity,
          v: `${productVariant.variantID}`,
        })
      }

      if (openCartAfterAddingItems) {
        toggleCart(true)
      }
    }
  }

  return (
    <span className={cx('flex items-center', className)}>
      <span className={cx('mr-4 text-xs whitespace-nowrap')}>
        {getPrice(productVariant.price, taxRate)} {currency}
      </span>

      <Button
        id="product-add-to-cart-button"
        variant={variant}
        size={size}
        color={color}
        icon={icon}
        iconAlignment={iconAlignment}
        onClick={handleAddToCart}
        className={buttonClassName}
        disabled={
          !cart || !productVariant.inStock || isAddingToCart || isAddToCartError
        }
      >
        {!cart && strings.buttonUnavailable}
        {isAddingToCart && strings.buttonAdding}
        {!productVariant.inStock && strings.productOutOfStock}
        {cart &&
          productVariant.inStock &&
          !isAddingToCart &&
          !isAddToCartError &&
          children}
      </Button>
    </span>
  )
}

export default AddToCartButton
